import type { MedalKey } from '../../../../components/gamification/scorecardData';
import { adaptMedals } from '../../../../components/gamification/useLearnerGamification';
import { learnerActivity } from '../Program/components/learning-lifecycle/activityMeasures';
import { isCapabilityRelease } from '../../../../utils/capabilityRelease';
import type { IMemberProgram } from './MemberProgramsModal';
import type { IOrgMember } from './OrgMembersPage';

// Figures on the Org Members table and the column sort that orders by them.

// Average of a list of 0–100 values (rounded). Empty → 0.
export const average = (nums: number[]): number =>
  nums.length ? Math.round(nums.reduce((a, b) => a + b, 0) / nums.length) : 0;

// The member's programs the row reports on. The BE returns ALL of a member's
// programs even when the list is filtered, so scope to the selected program
// here; no filter → every enrolled program.
export const scopedPrograms = (member: IOrgMember, programFilter?: string): IMemberProgram[] => {
  const all = member.programs || [];
  return programFilter ? all.filter((p) => p.id === programFilter) : all;
};

// Program Progress as the row shows it. Null when there's nothing to average.
export const programProgress = (member: IOrgMember, programFilter?: string): number | null => {
  const programs = scopedPrograms(member, programFilter);
  return programs.length ? average(programs.map((p) => p.progress)) : null;
};

/**
 * The member's overall Learning Tier as their own dashboard shows it. With the
 * Capability release (utils/capabilityRelease.ts): the average of their
 * Activity measures, each counting equally (see learnerActivity). Before it:
 * the BE's overall tier, the lowest of the five. Null with no tier data.
 */
export const memberTier = (member: IOrgMember): MedalKey | null => {
  if (!member.developmentTier) return null;
  if (!isCapabilityRelease()) return member.developmentTier.overall ?? null;
  return learnerActivity(adaptMedals(member.developmentTier).sections, member.capabilityActivity).tier;
};

export type SortKey = 'name' | 'lastLogin' | 'tier' | 'progress';
export type SortDir = 'asc' | 'desc';

// Rust lowest, Platinum highest.
const TIER_ORDER: MedalKey[] = ['wood', 'bronze', 'silver', 'gold', 'platinum'];

const sortValue = (member: IOrgMember, key: SortKey, programFilter?: string): string | number | null => {
  switch (key) {
    case 'name':
      return (member.fullName || '').trim().toLowerCase() || null;
    case 'lastLogin':
      return member.lastLogin || null;
    case 'tier': {
      const tier = memberTier(member);
      return tier ? TIER_ORDER.indexOf(tier) : null;
    }
    case 'progress':
      return programProgress(member, programFilter);
    default:
      return null;
  }
};

/**
 * Members ordered by one column. Blanks (never logged in, no tier, no
 * programs) sit at the bottom whichever way the column runs, so flipping the
 * order brings the other end of the real values to the top.
 */
export const sortMembers = (
  members: IOrgMember[],
  sort: { key: SortKey; dir: SortDir } | null,
  programFilter?: string
): IOrgMember[] => {
  if (!sort) return members;
  const sign = sort.dir === 'asc' ? 1 : -1;
  return members
    .map((member) => ({ member, value: sortValue(member, sort.key, programFilter) }))
    .sort((a, b) => {
      if (a.value === null || b.value === null) return a.value === b.value ? 0 : a.value === null ? 1 : -1;
      const cmp = typeof a.value === 'string' ? a.value.localeCompare(String(b.value)) : a.value - (b.value as number);
      return cmp * sign;
    })
    .map(({ member }) => member);
};
